// src/components/SearchProviderConfig/SearchProviderConfig.tsx
import React from 'react';
import { SearchProvider } from '@elastic/react-search-ui';
import { createAppSearchConnector } from '../../utils/apiConnector';
import { getCurrentTenant } from '../../config/tenants';
import { SearchDriverOptions } from '@elastic/search-ui';
import ApplyExactFilter from '../ApplyExactFilter/ApplyExactFilter';

interface SearchProviderConfigProps {
  children: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Shared fields — present in BOTH Porsche and Mercedes Elastic engines
// ---------------------------------------------------------------------------
const SHARED_FACETS = {
  listing_make_txt:               { type: 'value', size: 30 },
  listing_model_txt:              { type: 'value', size: 30 },
  listing_model_generation_txt:   { type: 'value', size: 30 },
  listing_transmission_txt:       { type: 'value', size: 30 },
  listing_body_type_txt:          { type: 'value', size: 30 },
  listing_pab_variant_txt:        { type: 'value', size: 30 },
  listing_production_year:        { type: 'value', size: 30 },
  listing_grouped_exterior_color: { type: 'value', size: 30 },
  listing_grouped_base_color:     { type: 'value', size: 30 },
  listing_country_txt:            { type: 'value', size: 30 },
  listing_country_code_txt:       { type: 'value', size: 30 },
  listing_price_value:            { type: 'value', size: 30 },
  listing_mileage_value:          { type: 'value', size: 30 },
  listing_variant_sub_group:      { type: 'value', size: 100 },
  listing_variant_top_group:      { type: 'value', size: 100 },
  listing_ps:                     { type: 'value', size: 30 },
  listing_interior_base_color:    { type: 'value', size: 30 },
  listing_road_legal:             { type: 'value', size: 30 },
  listing_classic_car:            { type: 'value', size: 30 },
  listing_already_sold:           { type: 'value', size: 30 },
  listing_expert_appraisal:       { type: 'value', size: 30 },
  listing_build:                  { type: 'value', size: 30 },
  car_uuid:                       { type: 'value', size: 30 },
  scraped_at: {
    type: 'range',
    ranges: [
      { name: 'January 2025', from: '2025-01-01T00:00:00Z', to: '2025-01-31T23:59:59Z' },
      { name: 'February 2025', from: '2025-02-01T00:00:00Z', to: '2025-02-28T23:59:59Z' },
      { name: '2024', from: '2024-01-01T00:00:00Z', to: '2024-12-31T23:59:59Z' },
    ],
  },
} as const;

// ---------------------------------------------------------------------------
// Porsche-only facets — fields that exist only in the Porsche engine
// ---------------------------------------------------------------------------
const PORSCHE_ONLY_FACETS = {
  porsche_image_boost:         { type: 'value', size: 30 },
  porsche_price_boost:         { type: 'value', size: 30 },
  listing_porsche_cup:         { type: 'value', size: 30 },
  listing_fia:                 { type: 'value', size: 30 },
  listing_aco:                 { type: 'value', size: 30 },
  listing_porsche_cup_details: { type: 'value', size: 30 },
  listing_fia_details:         { type: 'value', size: 30 },
  listing_aco_details:         { type: 'value', size: 30 },
} as const;

// ---------------------------------------------------------------------------
// Mercedes-only facets — fields that exist only in the Mercedes engine
// ---------------------------------------------------------------------------
const MERCEDES_ONLY_FACETS = {
  listing_engine_type: { type: 'value', size: 10 },
} as const;

// ---------------------------------------------------------------------------
// Shared disjunctive facets
// ---------------------------------------------------------------------------
const SHARED_DISJUNCTIVE_FACETS = [
  'listing_make_txt',
  'listing_model_txt',
  'listing_model_generation_txt',
  'listing_transmission_txt',
  'listing_body_type_txt',
  'listing_pab_variant_txt',
  'listing_country_code_txt',
  'listing_production_year',
  'listing_grouped_exterior_color',
  'listing_grouped_base_color',
  'listing_country_txt',
  'listing_price_value',
  'listing_mileage_value',
  'listing_ps',
  'listing_variant_top_group',
  'listing_variant_sub_group',
  'listing_interior_base_color',
  'listing_road_legal',
  'listing_classic_car',
  'listing_already_sold',
  'listing_expert_appraisal',
  'listing_build',
  'scraped_at',
 
  'car_uuid',
];

const PORSCHE_ONLY_DISJUNCTIVE_FACETS = [
  'porsche_image_boost',
  'porsche_price_boost',
  'listing_porsche_cup',
  'listing_fia',
  'listing_aco',
  'listing_porsche_cup_details',
  'listing_fia_details',
  'listing_aco_details',
  'extraction_date',
];

const MERCEDES_ONLY_DISJUNCTIVE_FACETS = [
  'listing_engine_type',
];

// ---------------------------------------------------------------------------
// Shared result fields
// ---------------------------------------------------------------------------
const SHARED_RESULT_FIELDS = {
  listing_id:                     { raw: {} },
  listing_make_txt:               { raw: {} },
  listing_model_txt:              { raw: {} },
  listing_model_generation_txt:   { raw: {} },
  listing_production_year:        { raw: {} },
  listing_transmission_txt:       { raw: {} },
  listing_ps:                     { raw: {} },
  listing_body_type_txt:          { raw: {} },
  listing_variant_txt:            { raw: {} },
  listing_pab_variant_txt:        { raw: {} },
  listing_price_txt:              { raw: {} },
  listing_price_currency:         { raw: {} },
  listing_price_value:            { raw: {} },
  listing_engine_txt:             { raw: {} },
  listing_mileage_txt:            { raw: {} },
  listing_mileage_value:          { raw: {} },
  listing_location_txt:           { raw: {} },
  listing_country_txt:            { raw: {} },
  listing_city_txt:               { raw: {} },
  listing_country_code_txt:       { raw: {} },
  listing_date_lastindex:         { raw: {} },
  listing_date_firstindex:        { raw: {} },
  listing_date_publication_txt:   { raw: {} },
  listing_date_publication_value: { raw: {} },
  listing_exterior_color_txt:     { raw: {} },
  listing_grouped_exterior_color: { raw: {} },
  listing_grouped_base_color:     { raw: {} },
  listing_interior_color_txt:     { raw: {} },
  listing_image_url:              { raw: {} },
  listing_url:                    { raw: {} },
  listing_variant_sub_group:      { raw: {} },
  listing_variant_top_group:      { raw: {} },
  listing_interior_base_color:    { raw: {} },
  listing_road_legal:             { raw: {} },
  listing_classic_car:            { raw: {} },
  listing_already_sold:           { raw: {} },
  listing_expert_appraisal:       { raw: {} },
  listing_build:                  { raw: {} },
  scraped_at:                     { raw: {} },
  
  config_hash_exact:              { raw: {} },
  config_hash_similar:            { raw: {} },
  car_uuid:                       { raw: {} },
};

// Porsche-only result fields
const PORSCHE_ONLY_RESULT_FIELDS = {
  porsche_image_boost:         { raw: {} },
  porsche_price_boost:         { raw: {} },
  listing_porsche_cup:         { raw: {} },
  listing_fia:                 { raw: {} },
  listing_aco:                 { raw: {} },
  listing_porsche_cup_details: { raw: {} },
  extraction_date:                { raw: {} },
  listing_fia_details:         { raw: {} },
  listing_aco_details:         { raw: {} },
};

// Mercedes-only result fields
const MERCEDES_ONLY_RESULT_FIELDS = {};

// ---------------------------------------------------------------------------
// Shared search fields
// ---------------------------------------------------------------------------
const SHARED_SEARCH_FIELDS = {
  listing_make_txt:               {},
  listing_model_txt:              {},
  listing_model_generation_txt:   {},
  listing_variant_txt:            {},
  listing_pab_variant_txt:        {},
  listing_engine_txt:             {},
  listing_transmission_txt:       {},
  listing_body_type_txt:          {},
  listing_location_txt:           {},
  listing_production_year:        {},
  listing_country_txt:            {},
  listing_exterior_color_txt:     {},
  listing_grouped_exterior_color: {},
  listing_city_txt:               {},
  listing_price_txt:              {},
  listing_price_currency:         {},
  listing_price_value:            {},
  listing_mileage_txt:            {},
  listing_mileage_value:          {},
  listing_date_lastindex:         {},
  listing_date_firstindex:        {},
  listing_date_publication_txt:   {},
  listing_date_publication_value: {},
  listing_country_code_txt:       {},
  listing_grouped_base_color:     {},
  listing_interior_color_txt:     {},
  listing_image_url:              { weight: 1 },
  listing_variant_sub_group:      {},
  listing_variant_top_group:      {},
  listing_interior_base_color:    {},
  listing_road_legal:             {},
  listing_classic_car:            {},
  listing_already_sold:           {},
  listing_expert_appraisal:       {},
  listing_build:                  {},
  scraped_at:                     {},

  car_uuid:                       {},
};

// Porsche-only search fields
const PORSCHE_ONLY_SEARCH_FIELDS = {
  porsche_image_boost:         { weight: 1000 },
  listing_horsepower_txt:      {},
  listing_porsche_cup:         {},
  listing_fia:                 {},
  listing_aco:                 {},
  listing_porsche_cup_details: {},
  listing_fia_details:         {},
  extraction_date:                {},
  listing_aco_details:         {},
};

// Mercedes-only search fields
const MERCEDES_ONLY_SEARCH_FIELDS = {};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SearchProviderConfig: React.FC<SearchProviderConfigProps> = ({
  children,
}) => {
  const connector = createAppSearchConnector();
  const tenant    = getCurrentTenant();

  if (!connector) {
    return <div>Error: API configuration is missing</div>;
  }

  const isPorsche  = tenant.id === 'porsche';
  const isMercedes = tenant.id === 'mercedes';

  // Build tenant-aware field sets
  const tenantFacets = {
    ...SHARED_FACETS,
    ...(isPorsche  ? PORSCHE_ONLY_FACETS  : {}),
    ...(isMercedes ? MERCEDES_ONLY_FACETS : {}),
  };

  const tenantDisjunctiveFacets = [
    ...SHARED_DISJUNCTIVE_FACETS,
    ...(isPorsche  ? PORSCHE_ONLY_DISJUNCTIVE_FACETS  : []),
    ...(isMercedes ? MERCEDES_ONLY_DISJUNCTIVE_FACETS : []),
  ];

  const tenantResultFields = {
    ...SHARED_RESULT_FIELDS,
    ...(isPorsche  ? PORSCHE_ONLY_RESULT_FIELDS  : {}),
    ...(isMercedes ? MERCEDES_ONLY_RESULT_FIELDS : {}),
  };

  const tenantSearchFields = {
    ...SHARED_SEARCH_FIELDS,
    ...(isPorsche  ? PORSCHE_ONLY_SEARCH_FIELDS  : {}),
    ...(isMercedes ? MERCEDES_ONLY_SEARCH_FIELDS : {}),
  };

  // Image boost sort field — Porsche uses porsche_image_boost,
  // Mercedes doesn't have it so we fall back to production year desc.
  // App Search requires { [fieldName]: 'asc'|'desc' } — NOT { field, direction }.
  const initialSort = isPorsche
    ? [{ porsche_image_boost: 'desc' } as unknown as any]
    : [{ listing_production_year: 'desc' } as unknown as any];

  const config: SearchDriverOptions = {
    alwaysSearchOnInitialLoad: true,
    apiConnector: connector,
    hasA11yNotifications: true,
    trackUrlState: true,

    initialState: {
      resultsPerPage: 12,
    },

    searchQuery: {
      sort: initialSort,
      disjunctiveFacets: tenantDisjunctiveFacets,
      facets:        tenantFacets        as any,
      result_fields: tenantResultFields  as any,
      search_fields: tenantSearchFields  as any,

      // Automatically scope results to the current brand.
      // Works whether you use one shared engine or separate engines per brand.
      filters: tenant.searchConfig?.brandFilter
        ? [
            {
              field:  'listing_make_txt',
              values: [tenant.searchConfig.brandFilter],
              type:   'all',
            },
          ]
        : [],
    },
  };

  return (
    <SearchProvider config={config}>
      <ApplyExactFilter />
      {children}
    </SearchProvider>
  );
};

export default SearchProviderConfig;
